import { useState, useEffect, useCallback } from "react";
import { ApiService } from "services";
import { toast } from "react-hot-toast";
import { userData } from "utils/userData";

export const useInstitutionDetails = (institutionId, boundType, seconds, indicatorBanks = [], outboundBank = "") => {
    const [data, setData] = useState({
        indicator: null,
        approvalRate: null,
        adminUsers: null, // Covers dashboardData5 and dashboardData6
        recentBanks: null,
        transactionPerformance: null,
        transaction: null, // dashboardData10
    });

    const [loading, setLoading] = useState(false);

    const fetchReport = useCallback(
        async (reportType) => {
            try {
                const banksParam = indicatorBanks.length ? `&indicatorBanks=${indicatorBanks.toString()}` : "";
                const outboundBankParam = outboundBank ? `&outboundBank=${outboundBank}` : "";
                const query = `/${institutionId}?direction=${boundType}&indicatorLastPeriod=${seconds}&reportType=${reportType}${banksParam}${outboundBankParam}`;
                const response = await ApiService.getInstitutionDashboard(query);
                return response;
            } catch (err) {
                console.error(`Error fetching ${reportType}:`, err);
                return null;
            }
        },
        [institutionId, boundType, seconds, indicatorBanks, outboundBank]
    );

    const fetchAllData = useCallback(async () => {
        if (!institutionId) return;

        setLoading(true);
        try {
            const [
                indicatorData,
                approvalRateData,
                adminUsersData,
                recentBanksData,
                perfData,
                transactionData,
            ] = await Promise.all([
                fetchReport("indicator"),
                fetchReport("approvalRate"),
                fetchReport("adminUsers"),
                fetchReport("recentBanks"),
                fetchReport("transactionPerformance"),
                fetchReport("transaction"),
            ]);

            setData({
                indicator: indicatorData,
                approvalRate: approvalRateData,
                adminUsers: adminUsersData,
                recentBanks: recentBanksData,
                transactionPerformance: perfData,
                transaction: transactionData,
            });
        } catch (err) {
            console.error(err);
            toast.error("Failed to load institution details");
        } finally {
            setLoading(false);
        }
    }, [fetchReport, institutionId]);

    // Initial load
    useEffect(() => {
        fetchAllData();
    }, [fetchAllData]);

    // Interval update for adminUsers (refreshes every 15s)
    useEffect(() => {
        if (!institutionId) return;

        const role = userData?.userDetail?.role;
        const allowedRoles = ["chiefAdmin", "subAdmin1", "subAdmin2"];

        if (!allowedRoles.includes(role)) return;

        const interval = setInterval(async () => {
            const adminUsersData = await fetchReport("adminUsers");
            if (adminUsersData) {
                setData((prev) => ({ ...prev, adminUsers: adminUsersData }));
            }
        }, 15000);

        return () => clearInterval(interval);
    }, [fetchReport, institutionId]);

    return {
        data,
        loading,
        refetch: fetchAllData,
    };
};
